import {
  Dispatch,
  FC,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { SupportedFileInput } from '@uploadcare/upload-client';
import { startCase } from 'lodash';
import {
  useCreateNote,
  useDeleteNote,
  useGetNotes,
  useUpdateNote,
} from 'modules/account/contact/hooks';
import { useGetSubscription } from 'modules/account/settings/hooks';
import { Button, DeleteItemModal, Entry } from 'ui/components/common';
import { ENTRY_TYPE } from 'ui/components/common/Entry/Entry';
import { Composer } from 'ui/partials';
import useModal from 'ui/partials/Modal/useModal';
import { COMPOSER_TYPES } from 'utils/constants';
import { useUploadToUploadCare } from 'utils/helpers';
import { Comment, COMMENT_TYPE } from 'utils/types';
import { useQueryParams } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { Dropdown } from 'ui/components/nav';
import { useAppWrapperContext } from 'ui/layouts/AppWrapper';

interface CONTEXT {
  selectedNoteId: string | null;
  setSelectedNoteId: Dispatch<SetStateAction<string | null>>;
}

export const LeadNotesContext = createContext<CONTEXT | null>(null);
// eslint-disable-next-line react-refresh/only-export-components
export const useLeadNotesContext = () => useContext(LeadNotesContext);

interface PROPS {
  id: string;
  source: string;
}

const Notes: FC<PROPS> = ({ id, source }) => {
  const [queryParams] = useQueryParams();
  const navigate = useNavigate();
  const params = useParams();
  const [openNotesComposer, setOpenNotesComposer] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const { showModal } = useModal();
  const { mutate: addNote, isLoading: createNoteLoading } = useCreateNote(id);
  const { mutate: deleteNote, isLoading: deleteLoading } = useDeleteNote();
  const { mutate: updateNote } = useUpdateNote();
  const { mutateAsync: uploadFiles, isLoading: attachmentLoading } =
    useUploadToUploadCare();
  const {
    state: { relativePath, isPreview },
  } = useAppWrapperContext();

  const [commentType, setCommentType] = useState<COMMENT_TYPE | 'ALL'>('ALL');

  useEffect(() => {
    if (queryParams?.['type']) {
      setOpenNotesComposer(true);
    }
  }, [queryParams]);

  const query = {
    ...(commentType !== 'ALL' && { type: commentType }),
    referenceId: id,
    'include[0]': 'comments',
    'include[1]': 'attachments',
    'include[2]': 'accountUser',
  };

  const { data: subscription } = useGetSubscription();
  const { data: notes } = useGetNotes(query);

  const closeComposer = useCallback(() => {
    setOpenNotesComposer(false);
    setSelectedNoteId(null);

    if (isPreview) navigate(`${relativePath}contact/leads/${params.id}/notes`);
    else navigate(`/contact/leads/${params.id}/notes`);
  }, [navigate, params.id, relativePath, isPreview]);

  const onSubmit = async (data: {
    body: string;
    attachments: SupportedFileInput[];
    type?: COMMENT_TYPE;
  }) => {
    const uploadedAttachments =
      data?.attachments.length && (await uploadFiles(data.attachments));

    addNote({
      type: data.type ?? COMMENT_TYPE.GENERAL,
      content: data.body,
      referenceId: id,
      source: startCase(source),
      ...(uploadedAttachments && {
        attachments: uploadedAttachments.files.map((file) => ({
          fileUrl: file.cdnUrl,
          fileType: file.mimeType,
          fileName: file.originalFilename,
          fileSize: file.size,
          source: startCase(source),
          ...(file.isImage && { fileThumbnail: file.cdnUrl }),
        })),
      }),
    } as Comment);
  };

  const handleDelete = (noteId: string) => {
    deleteNote({ noteId, id: noteId, referenceId: id });
  };

  const showDeleteModal = (noteId: string) => {
    showModal({
      title: 'Notes',
      disableOutsideClick: true,
      content: (
        <DeleteItemModal
          onClickDelete={() => handleDelete(noteId)}
          isLoading={deleteLoading}
          validation
        />
      ),
    });
  };

  const handleUpdateNote = (
    _id: string,
    value: Comment,
    referenceId: string,
  ) => {
    updateNote({ id: _id, payload: value, referenceId });
  };

  const handleOpenNewNote = () => {
    setOpenNotesComposer(true);
    setSelectedNoteId('NEW-NOTE');
  };

  const value = useMemo(
    () => ({ selectedNoteId, setSelectedNoteId }),
    [selectedNoteId],
  );

  return (
    <LeadNotesContext.Provider value={value}>
      <div className="mb-5 flex justify-between items-center">
        <Button
          onClick={handleOpenNewNote}
          label="CREATE NOTE"
          size="sm"
          disabled={!!selectedNoteId || subscription?.isExpired}
          expiredSubscription={subscription?.isExpired}
          tooltipPosition="right"
        />
        <Dropdown
          className="px-4 py-1"
          options={[
            { label: 'All', value: 'ALL' },
            { label: 'General', value: COMMENT_TYPE.GENERAL },
            { label: 'Call', value: COMMENT_TYPE.CALL },
            { label: 'SMS', value: COMMENT_TYPE.SMS },
          ]}
          onChange={(val) => {
            setCommentType(val.value as COMMENT_TYPE);
          }}
          value={commentType}
        />
      </div>
      <div className="max-h-[calc(100vh-299px)] overflow-y-auto">
        {!notes?.length ? (
          <p className="mb-[14px] text-sm leading-5">
            Take notes about this record to keep track of important info.
          </p>
        ) : (
          notes?.map((entry, index) => (
            <Entry
              onDelete={() => showDeleteModal(entry.id)}
              onEdit={handleUpdateNote}
              open={index === 0}
              key={entry?.id}
              data={entry}
              type={ENTRY_TYPE.NOTES}
            />
          ))
        )}
      </div>
      <Composer
        type={COMPOSER_TYPES.note}
        onSubmit={onSubmit}
        closeComposer={closeComposer}
        isOpen={openNotesComposer}
        isLoading={createNoteLoading || attachmentLoading}
      />
    </LeadNotesContext.Provider>
  );
};

export default Notes;
